import { isDOMElement } from '../../utils/dom-utils';
import { handleCarouselKeyboardNavigation } from '../../utils/carousel-utils';

import {
  buildCarousel,
  gotoNextSlide,
  gotoPreviousSlide,
  dynamicAppendElements,
  jumpToSlide,
  destroyCarousel,
} from './carousel';

const INITIAL_ITEMS_TO_CREATE = 18;
const VIDEO_CAROUSEL_CARD = 'c-video-card';

const isArrowsInPage = () => {
  return document.getElementsByClassName('prev')[0] && document.getElementsByClassName('next')[0];
};

/**
 * Callback triggered on next slide
 * @param {number} slideIndex the current slide index
 * @param {boolean} carouselEndReached true if the carousel reach the tail otherwise false
 */
const onNextSlide = (slideIndex, carouselEndReached) => {
  if (isArrowsInPage()) {
    document.getElementsByClassName('prev')[0].setAttribute('style', 'display: flex;');
    if (carouselEndReached) {
      document.getElementsByClassName('next')[0].setAttribute('style', 'display: none;');
    }
  }
};

/**
 * Callback triggered on previous slide
 * @param {number} slideIndex the current slide index
 * @param {boolean} carouselStartReached true if the carousel reach the top otherwise false
 */
const onPreviousSlide = (slideIndex, carouselStartReached) => {
  if (isArrowsInPage()) {
    document.getElementsByClassName('next')[0].setAttribute('style', 'display: flex;');
    if (carouselStartReached) {
      document.getElementsByClassName('prev')[0].setAttribute('style', 'display: none;');
    }
  }
};

/**
 * Reset the arrows to firs slide
 */
const resetArrowsToFirstSlide = () => {
  if (isArrowsInPage()) {
    document.getElementsByClassName('next')[0].setAttribute('style', 'display: flex;');
    document.getElementsByClassName('prev')[0].setAttribute('style', 'display: none;');
  }
};

/**
 * Runs immediately after initialization
 */
const onCarouselInit = (elements) => {
  /*
    TODO: onCarouselInit
  */
};

/**
 * Runs after slide change
 * @param {*} params the params passed as argument in carousel build phase
 */
// eslint-disable-next-line no-unused-vars
const onCarouselChanged = (params) => {
  /*
    TODO: add dynamic append
  */
};

/**
 * Destroy Remove all event listeners on carousel instance and restore the initial
 * markup inside selector
 * @param {object} carousel the carousel to destroy
 */
const destroyElementsInCarousel = (carousel) => {
  destroyCarousel(carousel, true);
};

/**
 * Populate the carousel
 * @param {array} elementArray array of elements for populate the carousel
 * @param {string} carouselId the Id of the carousel to populate
 * @param {function} cardTemplateCallback the callback for build a HTML template of each element
 * @param {function} cardCallback the callback triggered by each element (passed as argument to cardTemplateCallback)
 */
const populateCarousel = (elementArray, carouselId, cardTemplateCallback, cardCallback) => {
  const container = document.querySelector(`#${carouselId}`);
  if (container) {
    for (const elementCard of elementArray) {
      const element = cardTemplateCallback(elementCard, cardCallback);
      if (isDOMElement(element)) {
        container.appendChild(element);
      } else {
        throw new Error('init-carousel.js - populateCarousel() : Try to append Node that not is a valid DOM element');
      }
    }
  }
};

/**
 * Build a array of HTML elements
 * @param {array} carouselData the array of elements
 * @param {function} cardTemplateCallback the callback for build a HTML template of each element
 * @param {function} cardCallback the callback to trigger on each element (passed as argument to cardTemplateCallback)
 */
const populateElementArray = (carouselData, cardTemplateCallback, cardCallback) => {
  const arrayOfHTMLElements = [];
  for (const carouselElement of carouselData) {
    const htmlElement = cardTemplateCallback(carouselElement, cardCallback);
    if (isDOMElement(htmlElement)) {
      arrayOfHTMLElements.push(htmlElement);
    } else {
      throw new Error('init-carousel.js - populateElementArray() : Try to create Node that not is a valid DOM element');
    }
  }
  return arrayOfHTMLElements;
};

/**
 * Detach events to arrows
 * @param {listener} previousSlideButtonClicked listener on prev arrow - type:click
 * @param {listener} gotoNextSlideWithDynamicAppend listener on next arrow - type:click
 */
const detachEvents = (previousSlideButtonClicked, gotoNextSlideWithDynamicAppend) => {
  if (isArrowsInPage()) {
    document.querySelector('.prev').removeEventListener('click', previousSlideButtonClicked);
    document.querySelector('.prev').removeEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        previousSlideButtonClicked();
      }
    });
    document.querySelector('.next').removeEventListener('click', gotoNextSlideWithDynamicAppend);
    document.querySelector('.next').removeEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        gotoNextSlideWithDynamicAppend();
      }
    });
  }
};

/**
 * Attach events to arrows
 * @param {listener} previousSlideButtonClicked listener on prev arrow - type:click
 * @param {listener} gotoNextSlideWithDynamicAppend listener on next arrow - type:click
 */
const attachEvents = (previousSlideButtonClicked, gotoNextSlideWithDynamicAppend) => {
  if (isArrowsInPage()) {
    document.querySelector('.prev').addEventListener('click', previousSlideButtonClicked);
    document.querySelector('.prev').addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        previousSlideButtonClicked();
      }
    });
    document.querySelector('.next').addEventListener('click', gotoNextSlideWithDynamicAppend);
    document.querySelector('.next').addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        gotoNextSlideWithDynamicAppend();
      }
    });
  }
};

/**
 * A function that initialize new generic Carousel
 * @param {string} carouselId The selector to use as a carousel.
 * @param {array} carouselData The carousel data array
 * @param {function} cardTemplateCallback The callback to build HTML template accordig to carousel data array
 * @param {function} cardCallback The callback to call on single card event e.g. onclick event on card.
 * @param {object}  carouselConfig the configuration object of the carousel
 * @returns {object} functions for manage carousel (destroy and slides navigation)
 */
export const initCarousel = (carouselId, data, cardTemplateCallback, cardCallback, carouselConfig = {}) => {
  const carouselData = [...data];
  const elementsLength = carouselData.length;
  let appendCount = 1;
  const initialData = carouselData.slice(0, INITIAL_ITEMS_TO_CREATE);
  carouselData.splice(0, INITIAL_ITEMS_TO_CREATE);

  let elementArray = populateElementArray(carouselData, cardTemplateCallback, cardCallback);

  populateCarousel(initialData, carouselId, cardTemplateCallback, cardCallback);

  const carousel = buildCarousel(`.${carouselId}`, carouselConfig, onCarouselInit, onCarouselChanged, elementArray);

  const unbindKeyboardNavigation = handleCarouselKeyboardNavigation({ carousel, slideSelector: VIDEO_CAROUSEL_CARD });

  const gotoNextSlideWithDynamicAppend = () => {
    gotoNextSlide(carousel, onNextSlide, elementsLength);
    const { currentSlide, perPage } = carousel;
    if (currentSlide >= perPage * appendCount && currentSlide >= INITIAL_ITEMS_TO_CREATE * appendCount - perPage) {
      elementArray = dynamicAppendElements(carousel, elementArray, INITIAL_ITEMS_TO_CREATE);
      appendCount++;
    }
    return currentSlide;
  };

  const jumpToSlideWithDynamicAppend = (slideIndex) => {
    if (slideIndex < elementsLength) {
      const elementsInPage = elementsLength - elementArray.length;
      const slideInPage = slideIndex + carousel.perPage <= elementsInPage || elementArray.length <= carousel.perPage;
      if (slideIndex > carousel.currentSlide) {
        onNextSlide(slideIndex, slideIndex + carousel.perPage >= elementsLength);
      }
      if (slideInPage) {
        jumpToSlide(carousel, slideIndex);
      } else {
        elementArray = dynamicAppendElements(carousel, elementArray, INITIAL_ITEMS_TO_CREATE);
        jumpToSlide(carousel, slideIndex);
      }
      return true;
    }
    return false;
  };

  const previousSlideButtonClicked = () => {
    gotoPreviousSlide(carousel, onPreviousSlide);
  };

  attachEvents(previousSlideButtonClicked, gotoNextSlideWithDynamicAppend);

  return {
    gotoNextSlide: gotoNextSlideWithDynamicAppend,
    jumpToSlide: jumpToSlideWithDynamicAppend,
    destroy: () => {
      detachEvents(previousSlideButtonClicked, gotoNextSlideWithDynamicAppend);
      resetArrowsToFirstSlide();
      destroyElementsInCarousel(carousel);
      unbindKeyboardNavigation();
    },
  };
};
